import {
	CommunityStatus,
	useDeleteManyProductMutation,
	useGetAllProductsQuery,
	useGetBrandQuery,
	useGetOrganizationQuery,
	useGetProductStatsQuery,
	usePublishProductsToCommunityMutation,
	useUnpublishProductsFromCommunityMutation,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import {
	Box,
	Building2,
	Copy,
	FileText,
	Globe,
	GlobeLock,
	LayoutGrid,
	List,
	Pencil,
	Plus,
	Shield,
	Trash2,
	Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { SelectionBar } from '../../components/ui/selection-bar';
import { SearchInput } from '../../components/ui/search-input';
import { ViewToggle } from '../../components/ui/view-toggle';
import { Tabs } from '../../components/ui/tabs';
import { BrandSelector } from '../../components/selectors/BrandSelector';
import { OrganizationSelector } from '../../components/selectors/OrganizationSelector';
import { AssignToClientModal } from './AssignToClientModal';
import { cn, formatDate } from '../../lib/utils';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { TableSkeleton } from '../../components/ui/list-skeletons';
import { EmptyState } from '../../components/ui/empty-state';
import { CreateProductModal } from './CreateProductModal';
import { AddToQuoteModal } from '../../components/AddToQuoteModal';
import { EditProductModal } from './EditProductModal';
import { DuplicateProductModal } from './DuplicateProductModal';
import { ProductCard } from '../../components/ProductCard';
import { ProductActionMenu, type ProductAction } from '../../components/products/ProductActionMenu';
import { ProductImage, type DisplayCrop } from '../../components/ui/product-image';

/* ── Product image / color swatch ── */

function ProductThumb({
	images,
	name,
}: {
	images?: Array<{
		url?: string | null;
		isPrimary?: boolean | null;
		crop?: DisplayCrop | null;
	}> | null;
	name: string;
}) {
	const primary = images?.find((i) => i.isPrimary) ?? images?.[0];

	if (primary?.url) {
		return (
			<ProductImage
				image={{ url: primary.url, crop: primary.crop, name }}
				className="h-9 w-9 shrink-0 rounded-[var(--r-1)] border border-line-1"
			/>
		);
	}
	return (
		<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-1)] border border-line-1 bg-surface-fill">
			<Box size={14} className="text-text-4" />
		</div>
	);
}

/* ── Product card image carousel ── */

/* ── Loading skeletons ── */

function GridSkeletons() {
	return (
		<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{Array.from({ length: 8 }).map((_, i) => (
				<Card key={i} className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="animate-pulse bg-surface-fill" style={{ aspectRatio: '4/3' }} />
					<div className="p-3.5">
						<div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-surface-fill" />
						<div className="h-3 w-1/2 animate-pulse rounded bg-surface-fill" />
					</div>
				</Card>
			))}
		</div>
	);
}

/**
 * One row of products as laid out at the current viewport, for sizing scroll-mode batches.
 * Mirrors the grid below (1/2/3/4 columns at sm/lg/xl, 14px gap, square image + ~74px of
 * text) inside the layout's sidebar, content cap and page padding; a table row is the 36px
 * thumbnail plus cell padding.
 */
function productRows(view: string): RowEstimate {
	if (view !== 'grid') return { rowHeight: 57 };
	const vw = window.innerWidth;
	const columns = vw >= 1280 ? 4 : vw >= 1024 ? 3 : vw >= 640 ? 2 : 1;
	const sidebar = vw >= 768 ? 212 : 0;
	const padding = vw >= 768 ? 64 : vw >= 640 ? 48 : 32;
	const content = Math.min(vw - sidebar, 1440) - padding;
	const cardWidth = (content - (columns - 1) * 14) / columns;
	return { columns, rowHeight: cardWidth + 74 + 14 };
}

/* ════════════════════════ PRODUCTS PAGE ════════════════════════ */

export function ProductsPage() {
	const navigate = useNavigate();
	useSetPageMeta('Products', [{ label: 'Products' }]);
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		view,
		setView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		mode,
		setMode,
		request,
		loadMore,
		reload,
		limit,
	} = useListParams({
		defaultSort: 'name',
		defaultDir: 'asc',
		defaultView: 'grid',
		scrollRows: productRows,
	});
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [ownerParam, setOwnerTab] = useQueryParam('owner', 'all'); // 'all' | 'internal' | 'community' | 'clients'
	/* In the URL so the client page's "See all" can land here already filtered. */
	const [orgFilter, setOrgFilter] = useQueryParam('client', '');
	/* A client's brand — only offered once a client is picked, since brands
	   belong to one client. The brand page's "See all" lands here with both. */
	const [brandFilter, setBrandFilter] = useQueryParam('brand', '');
	const [selected, setSelected] = useState<Set<string>>(new Set());
	// The action being confirmed and the products it applies to: one product from a card or row
	// menu, or the selection from the selection bar. One state for both, so single and bulk run
	// through the same dialogs.
	const [pending, setPending] = useState<{
		kind: 'quote' | 'assign' | 'publish' | 'unpublish' | 'delete';
		ids: string[];
		fromSelection: boolean;
	} | null>(null);
	const [editProductId, setEditProductId] = useState<string | null>(null);
	const [duplicateTarget, setDuplicateTarget] = useState<{
		id: string;
		name: string;
		organizationId?: string | null;
		organizationName?: string | null;
		brandId?: string | null;
	} | null>(null);
	const [deleteManyProduct, { loading: deleteLoading }] = useDeleteManyProductMutation();
	const [publishToCommunity, { loading: publishLoading }] =
		usePublishProductsToCommunityMutation();
	const [unpublishFromCommunity, { loading: unpublishLoading }] =
		useUnpublishProductsFromCommunityMutation();

	const toggleSelect = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};
	const clearSelection = () => setSelected(new Set());

	const pendingIds = pending?.ids ?? [];
	const pendingCount = pendingIds.length;
	// `"Mug"` for one product, `3 products` for several — used in every confirm and toast.
	const pendingSubject = (names: Record<string, string>) =>
		pendingCount === 1
			? `"${names[pendingIds[0]] ?? 'this product'}"`
			: `${pendingCount} products`;

	// After an action: clear the selection only when the action came from it, so acting on one
	// product from its menu does not drop a selection the admin is building.
	const finishAction = () => {
		if (pending?.fromSelection) clearSelection();
		setPending(null);
		refresh();
	};

	const handleDelete = async () => {
		try {
			const { data } = await deleteManyProduct({ variables: { input: { ids: pendingIds } } });
			// All-or-nothing on the backend: success false means nothing was deleted.
			if (!data?.deleteManyProduct?.success) {
				toast.error(data?.deleteManyProduct?.message ?? 'Failed to delete');
				return;
			}
			toast.success(`${pendingSubject(productNames)} deleted`);
			finishAction();
		} catch {
			toast.error('Failed to delete');
		}
	};

	// Publishing is all-or-nothing on the backend: if any product cannot go live, nothing is
	// published and the errors say which and why.
	const handlePublish = async () => {
		try {
			const { data } = await publishToCommunity({ variables: { productIds: pendingIds } });
			const result = data?.publishProductsToCommunity;
			if (!result?.success) {
				const reasons = result?.errors?.map((e) => e.message).join('; ');
				toast.error(reasons ? `Nothing published — ${reasons}` : 'Failed to publish');
				return;
			}
			toast.success(`${pendingSubject(productNames)} on the community page`);
			finishAction();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to publish');
		}
	};

	const handleUnpublish = async () => {
		try {
			const { data } = await unpublishFromCommunity({
				variables: { productIds: pendingIds },
			});
			if (!data?.unpublishProductsFromCommunity?.success) {
				toast.error(data?.unpublishProductsFromCommunity?.message ?? 'Failed to unpublish');
				return;
			}
			toast.success(`${pendingSubject(productNames)} removed from the community page`);
			finishAction();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to unpublish');
		}
	};

	// Owner filter. A picked client narrows to that client and shows as the Clients tab. The other
	// tabs split the house products so each product appears in exactly one of them: Internal = not
	// assigned to a client and not on the community page (adminOnly + UNPUBLISHED), Community = on
	// the community page, Clients = assigned to any client (clientsOnly).
	// `admin` is the Internal tab's old name, kept so links handed out before the rename still work.
	const ownerTab = orgFilter ? 'clients' : ownerParam === 'admin' ? 'internal' : ownerParam;
	const organizationId = orgFilter || undefined;
	const adminOnly = !orgFilter && ownerTab === 'internal' ? true : undefined;
	const clientsOnly = !orgFilter && ownerTab === 'clients' ? true : undefined;
	const communityStatus = orgFilter
		? undefined
		: ownerTab === 'community'
			? CommunityStatus.Published
			: ownerTab === 'internal'
				? CommunityStatus.Unpublished
				: undefined;

	/* Products carry only the brand's id, so arriving from a brand's "See all"
	   the selector needs the name looked up. */
	const { data: brandData } = useGetBrandQuery({
		variables: { id: brandFilter },
		fetchPolicy: 'no-cache',
		skip: !brandFilter,
	});
	const brandLabel = brandData?.getBrand?.name ?? undefined;

	/* Same for the client: looked up, not read off the first product, which is
	   missing while loading and whenever the filtered list is empty. */
	const { data: orgData } = useGetOrganizationQuery({
		variables: { id: orgFilter },
		fetchPolicy: 'no-cache',
		skip: !orgFilter,
	});
	const orgLabel = orgData?.getOrganization?.name ?? undefined;

	const { data, loading, refetch } = useGetAllProductsQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
				organizationId,
				brandId: (orgFilter && brandFilter) || undefined,
				adminOnly,
				clientsOnly,
				communityStatus,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllProduct,
		loading
	);
	const products = list.items;
	const total = list.pageInfo?.totalCount ?? 0;
	const productNames: Record<string, string> = Object.fromEntries(
		products.map((p) => [p.id, p.name])
	);

	/**
	 * Every product action, in one order, for one product (its card/row menu) or the selection
	 * (the selection bar). Edit and Duplicate only exist for a single product. Publish is offered
	 * when every product is a house product and at least one is not yet published; Unpublish when
	 * at least one is published — the backend still has the final say on publishing.
	 */
	const actionsFor = (rows: typeof products, fromSelection: boolean): ProductAction[] => {
		const ids = rows.map((r) => r.id);
		const single = !fromSelection && rows.length === 1 ? rows[0] : null;
		const open = (kind: NonNullable<typeof pending>['kind']) => () =>
			setPending({ kind, ids, fromSelection });
		const actions: ProductAction[] = [];

		if (single) {
			actions.push(
				{
					id: 'edit',
					label: 'Edit',
					icon: Pencil,
					onClick: () => setEditProductId(single.id),
				},
				{
					id: 'duplicate',
					label: 'Duplicate',
					icon: Copy,
					onClick: () =>
						setDuplicateTarget({
							id: single.id,
							name: single.name,
							organizationId: single.organizationId,
							organizationName: single.organization?.name,
							brandId: single.brandId,
						}),
				}
			);
		}
		actions.push(
			{ id: 'quote', label: 'Add to quote', icon: FileText, onClick: open('quote') },
			{ id: 'assign', label: 'Assign to client', icon: Users, onClick: open('assign') }
		);
		if (rows.every((r) => !r.organizationId) && rows.some((r) => !r.communityPublishedAt)) {
			actions.push({
				id: 'publish',
				label: 'Publish to community',
				icon: Globe,
				onClick: open('publish'),
			});
		}
		if (rows.some((r) => r.communityPublishedAt)) {
			actions.push({
				id: 'unpublish',
				label: 'Unpublish',
				icon: GlobeLock,
				onClick: open('unpublish'),
			});
		}
		actions.push({
			id: 'delete',
			label: 'Delete',
			icon: Trash2,
			destructive: true,
			onClick: open('delete'),
		});
		return actions;
	};
	const selectedRows = products.filter((p) => selected.has(p.id));

	// Dataset-wide KPI stats (independent of the active tab/search/page).
	const { data: statsData, refetch: refetchStats } = useGetProductStatsQuery({
		fetchPolicy: 'no-cache',
	});
	const stats = statsData?.getProductStats;

	const refresh = () => {
		reload(refetch);
		refetchStats();
	};

	// Bound the selection to the rows on screen so bulk actions (e.g. add-to-quote org scoping)
	// always have full product context for what's selected. Scrolling keeps earlier rows on
	// screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelected(new Set());
	}, [shownPage, mode, debouncedSearch, ownerTab, orgFilter, brandFilter]);

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Products
					</h1>
					<p className="text-base text-text-3">
						Manage your branded product library — {total} products.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setCreateModalOpen(true)}>
						<Plus size={14} data-icon="inline-start" />
						Add product
					</Button>
				</div>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken sm:grid-cols-3 lg:grid-cols-5">
				{[
					{ label: 'Total', value: stats?.total ?? 0, sub: 'All products' },
					// Same split as the tabs: Internal excludes what is on the community page.
					{
						label: 'Internal',
						value: (stats?.admin ?? 0) - (stats?.community ?? 0),
						sub: 'Not assigned or published',
					},
					{
						label: 'Community',
						value: stats?.community ?? 0,
						sub: 'Shown to every client',
					},
					{
						label: 'Clients',
						value: stats?.organization ?? 0,
						sub: 'Assigned to a client',
					},
					{ label: 'Recent', value: stats?.recent ?? 0, sub: 'Last 30 days' },
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={ownerTab}
					onChange={(v) => {
						setOwnerTab(v);
						setOrgFilter('');
						setBrandFilter('');
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All' },
						{ value: 'internal', label: 'Internal' },
						{ value: 'community', label: 'Community' },
						{ value: 'clients', label: 'Clients' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search products…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<OrganizationSelector
						value={orgFilter}
						/* Arriving from a link, the selector has not seen the pick. */
						selectedLabel={orgLabel}
						onChange={(v) => {
							setOrgFilter(v);
							setBrandFilter('');
							if (v) setOwnerTab('clients');
							setPage(1);
						}}
						variant="toolbar"
						clearable
						placeholder="All organizations"
						className="w-[190px] shrink-0"
					/>
					{orgFilter && (
						<BrandSelector
							value={brandFilter}
							organizationId={orgFilter}
							selectedLabel={brandLabel}
							onChange={(v) => {
								setBrandFilter(v);
								setPage(1);
							}}
							variant="toolbar"
							clearable
							placeholder="All brands"
							className="w-[160px] shrink-0"
						/>
					)}
					<ViewToggle
						value={view}
						onChange={setView}
						options={[
							{ id: 'list', icon: List, label: 'List view' },
							{ id: 'grid', icon: LayoutGrid, label: 'Grid view' },
						]}
					/>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Content ── */}
			{list.loading ? (
				view === 'grid' ? (
					<GridSkeletons />
				) : (
					<TableSkeleton />
				)
			) : products.length === 0 ? (
				<EmptyState
					icon={Box}
					title="No products found"
					description={
						search
							? 'Try a different search term.'
							: 'No products have been created yet.'
					}
				/>
			) : view === 'grid' ? (
				/* ── Grid view (4 columns) ── */
				<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{products.map((p) => (
						<ProductCard
							key={p.id}
							name={p.name}
							sku={p.sku}
							images={p.images}
							tags={p.tags}
							organization={p.organization}
							isPublishedToCommunity={p.isPublishedToCommunity}
							isSelected={selected.has(p.id)}
							onSelect={() => toggleSelect(p.id)}
							onClick={() =>
								selected.size > 0
									? toggleSelect(p.id)
									: navigate(`/products/${p.id}`)
							}
							actions={actionsFor([p], false)}
						/>
					))}
				</div>
			) : (
				/* ── List view ── */
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[700px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<TableHead className="w-10">
										<Checkbox
											checked={
												selected.size === products.length &&
												products.length > 0
											}
											indeterminate={
												selected.size > 0 && selected.size < products.length
											}
											onCheckedChange={() => {
												if (selected.size === products.length) {
													clearSelection();
												} else {
													setSelected(new Set(products.map((p) => p.id)));
												}
											}}
										/>
									</TableHead>
									<SortableTableHead
										sortKey="name"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Product
									</SortableTableHead>
									<TableHead className="w-[170px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Owner
									</TableHead>
									<SortableTableHead
										sortKey="sku"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[130px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										SKU
									</SortableTableHead>
									<SortableTableHead
										sortKey="status"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[100px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Status
									</SortableTableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[110px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{products.map((p) => (
									<TableRow
										key={p.id}
										className={cn(
											'cursor-pointer border-line-1 transition-colors duration-[180ms] last:border-b-0',
											selected.has(p.id)
												? 'bg-accent-soft hover:bg-accent-soft'
												: 'hover:bg-surface-hover'
										)}
										onClick={() =>
											selected.size > 0
												? toggleSelect(p.id)
												: navigate(`/products/${p.id}`)
										}
									>
										<TableCell>
											<div onClick={(e) => e.stopPropagation()}>
												<Checkbox
													checked={selected.has(p.id)}
													onCheckedChange={() => toggleSelect(p.id)}
												/>
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-3">
												<ProductThumb images={p.images} name={p.name} />
												<span className="text-base font-medium text-text-1">
													{p.name}
												</span>
											</div>
										</TableCell>
										<TableCell>
											{p.organization ? (
												<div className="flex items-center gap-1.5 text-base text-text-2">
													{p.organization.logo ? (
														<img
															src={p.organization.logo}
															alt=""
															className="h-4 w-4 shrink-0 rounded-full object-cover"
														/>
													) : (
														<Building2
															size={12}
															className="shrink-0 text-text-4"
														/>
													)}
													<span className="truncate">
														{p.organization.name}
													</span>
												</div>
											) : p.isPublishedToCommunity ? (
												<div className="flex items-center gap-1.5 text-base text-accent">
													<Globe size={12} className="shrink-0" />
													Community
												</div>
											) : (
												<div className="flex items-center gap-1.5 text-base text-text-4">
													<Shield size={12} className="shrink-0" />
													Internal
												</div>
											)}
										</TableCell>
										<TableCell className="font-mono text-base text-text-2">
											{p.sku || '—'}
										</TableCell>
										<TableCell className="text-base text-text-2">
											{p.status || '—'}
										</TableCell>
										<TableCell className="text-base text-text-3">
											{p.createdAt ? formatDate(p.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<ProductActionMenu
													actions={actionsFor([p], false)}
												/>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			{/* ── Pagination / infinite scroll ── */}
			<ListFooter list={list} />

			<SelectionBar
				count={selected.size}
				actions={actionsFor(selectedRows, true)}
				onClear={clearSelection}
			/>

			{/* ── Action dialogs (one product or the selection) ── */}
			<AssignToClientModal
				open={pending?.kind === 'assign'}
				onOpenChange={(v) => !v && setPending(null)}
				productIds={pendingIds}
				onSuccess={finishAction}
			/>

			<AddToQuoteModal
				open={pending?.kind === 'quote'}
				onOpenChange={(v) => !v && setPending(null)}
				productIds={pendingIds}
				productNames={productNames}
				productOrganizations={Object.fromEntries(
					products.map((p) => [p.id, p.organizationId ?? null])
				)}
				productOrganizationNames={Object.fromEntries(
					products
						.filter((p) => p.organization?.name)
						.map((p) => [p.id, p.organization!.name])
				)}
				onSuccess={finishAction}
			/>

			<ConfirmDialog
				open={pending?.kind === 'publish'}
				onOpenChange={(v) => !v && setPending(null)}
				title="Publish to community"
				description={`${pendingSubject(productNames)} will be shown in every client's Community tab, where they can add a copy to their own products.${pendingCount > 1 ? " If any of them can't be published, none are." : ''}`}
				confirmLabel="Publish"
				variant="default"
				loading={publishLoading}
				onConfirm={handlePublish}
			/>

			<ConfirmDialog
				open={pending?.kind === 'unpublish'}
				onOpenChange={(v) => !v && setPending(null)}
				title="Unpublish from community"
				description={`${pendingSubject(productNames)} will be removed from clients' Community tab. Copies clients have already added are not affected.`}
				confirmLabel="Unpublish"
				variant="default"
				loading={unpublishLoading}
				onConfirm={handleUnpublish}
			/>

			<ConfirmDialog
				open={pending?.kind === 'delete'}
				onOpenChange={(v) => !v && setPending(null)}
				title={pendingCount > 1 ? 'Delete products' : 'Delete product'}
				description={`Are you sure you want to delete ${pendingSubject(productNames)}? This action cannot be undone.`}
				confirmLabel={pendingCount > 1 ? 'Delete all' : 'Delete'}
				loading={deleteLoading}
				onConfirm={handleDelete}
			/>

			{/* ── Edit product modal ── */}
			{editProductId && (
				<EditProductModal
					open={!!editProductId}
					onOpenChange={(v) => !v && setEditProductId(null)}
					productId={editProductId}
					onSaved={refresh}
				/>
			)}

			{/* ── Duplicate product modal ── */}
			<DuplicateProductModal
				open={!!duplicateTarget}
				onOpenChange={(v) => !v && setDuplicateTarget(null)}
				product={duplicateTarget}
				onSuccess={refresh}
			/>

			<CreateProductModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSuccess={refresh}
			/>
		</div>
	);
}
